import React, { useCallback, useState, useMemo } from "react";

import { Input, IDropdownOption } from "@treelab/panda";

import { IFilterValue } from "../../../../../../types/form-type";

import { getCollaborators } from "../../../../../../utils/tableData_secondary";
import { usePrevious } from "../../../../../../utils/hooks";
import useSortCollaboratorOptions from "../../../../../../utils/hooks/useSortCollaboratorOptions";
import { formatMessage } from "../../../../../../lib/intl";

import { ChipAvatar } from "./styled";
import { getIsMulti } from "../../../../../../utils/form_secondary";
import { ColumnType } from "../../../../../../types/table-type";

interface IProps {
  value: IFilterValue;
  onChange: (value: { [key: string]: any }) => void;
  columnType: ColumnType;
  shareUserId: string;
}

const getUnknownUser = () => ({
  nickName: formatMessage({ id: "other.dimension.unknown.user" }),
  color: "gray",
  smallImage: "/images/unknown_user_avatar.svg",
  id: `${new Date().toISOString()}_${Math.random().toString()}`,
});

const CollaboratorSelect: React.FC<IProps> = ({ value, onChange, columnType, shareUserId }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { collaboratorIds, collaboratorsById } = useMemo(() => getCollaborators(), []);

  const normalizedOptions: IDropdownOption[] = useMemo(
    () => {
      const collaboratorOptions = collaboratorIds
        .filter((id) => collaboratorsById[id])
        .map((collabId) => {
          const collab = collaboratorsById[collabId];

          return {
            name: collab?.nickName ?? "",
            value: collabId,
          };
        });

        if (shareUserId && columnType === ColumnType.CREATED_BY) {
          collaboratorOptions.unshift({
            name: '',
            value: shareUserId,
          });
        }

        return collaboratorOptions;
      },
    [collaboratorIds, collaboratorsById]
  );

  const prevIsDropdownOpen = usePrevious(isOpen);
  const [collaboratorOptions] = useSortCollaboratorOptions({
    basicOptions: normalizedOptions,
    selectedIds: value.filterString,
    isUpdateOptions: !prevIsDropdownOpen && isOpen,
  });

  const handleChange = useCallback(
    (data: any) => {
      onChange({ filterString: data });
    },
    [onChange]
  );

  return (
    <Input.Select
      input={{
        name: "1",
        onChange: handleChange,
        value: value.filterString,
      }}
      onOpen={setIsOpen}
      options={collaboratorOptions}
      isMulti={getIsMulti(columnType, value.filterCondition)}
      isRequired={false}
      includeSearch={true}
      meta={{}}
      renderValues={(values: string[]): React.ReactNode => {
        return (
          <>
            {values.map((value_) => {
              const collaborator = collaboratorsById[value_] || getUnknownUser();
              return (
                <ChipAvatar
                  key={value_}
                  isAnonymous={value_ === shareUserId}
                  name={collaborator.nickName}
                  userId={collaborator.id}
                  color={collaborator.color}
                  image={collaborator.smallImage}
                />
              );
            })}
          </>
        );
      }}
      renderItem={(option: IDropdownOption): React.ReactNode => {
        const collaborator = collaboratorsById[option.value];
        if (!collaborator) return null;
        return (
          <ChipAvatar
            isAnonymous={option.value === shareUserId}
            name={collaborator.nickName}
            userId={collaborator.id}
            color={collaborator.color}
            image={collaborator.smallImage}
          />
        );
      }}
    />
  );
};

export default CollaboratorSelect;
